var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
/* eslint-disable jsx-a11y/no-static-element-interactions */
import classNames from 'classnames';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { handleReadOnlyError, isAccountReadOnly, showReadOnlyNotice } from 'api/readOnlyAccess';
import useWebSocketMessage from 'hooks/useWebSocketMessage';
import PaperclipSvg from 'images/v5/icons/attachment-01.svg';
import BoldSvg from 'images/v5/icons/bold.svg';
import ItalicSvg from 'images/v5/icons/italic.svg';
import ListBulletedSvg from 'images/v5/icons/list.svg';
import ListNumberedSvg from 'images/v5/icons/numbered-list.svg';
import SendSvg from 'images/v5/icons/send-03.svg';
import UnderlineSvg from 'images/v5/icons/underline.svg';
import Button from 'layout/atoms/Button/Button';
import FileInput from 'layout/atoms/FileInput';
import MentionsDropdown from 'layout/atoms/MentionsDropdown/MentionsDropdown';
import { addChatMessage as addChatSliceMessage, fetchMembers, selectChatMembers, } from 'store/chatSlice';
import { addChatMessage as addInboxChatMessage, selectInboxConversation } from 'store/inboxSlice';
import { selectUserProfileDataData } from 'store/userSlice';
import { applyListFormatting, MESSAGE_FORMAT_MARKERS, wrapSelectionWithMarker, } from 'utils/messageFormatting';
import styles from './MessageInput.module.scss';
const formatButtons = [
    { key: 'bold', Icon: BoldSvg, marker: MESSAGE_FORMAT_MARKERS.bold },
    { key: 'italic', Icon: ItalicSvg, marker: MESSAGE_FORMAT_MARKERS.italic },
    { key: 'underline', Icon: UnderlineSvg, marker: MESSAGE_FORMAT_MARKERS.underline },
];
const listButtons = [
    { key: 'bulleted', Icon: ListBulletedSvg, type: 'bullet' },
    { key: 'numbered', Icon: ListNumberedSvg, type: 'numbered' },
];
function MessageInput({ conversationId, sliceType = 'inbox', onAfterSend, toolbarEnd, }) {
    const { t } = useTranslation(['chat']);
    const dispatch = useDispatch();
    const allMembers = useSelector(selectChatMembers);
    const conversation = useSelector(selectInboxConversation);
    const currentUser = useSelector(selectUserProfileDataData);
    const { sendMessage: sendWebSocketMessage } = useWebSocketMessage();
    const addMessage = sliceType === 'chat' ? addChatSliceMessage : addInboxChatMessage;
    const inputRef = useRef(null);
    const [isFocused, setIsFocused] = useState(false);
    const [form, setForm] = useState({
        conversationId,
        message: '',
        image: null,
    });
    const [mentionsState, setMentionsState] = useState({
        showDropdown: false,
        filteredUsers: [],
        selectedIndex: 0,
        mentionStart: 0,
        searchText: '',
        mentions: [],
    });
    const [mentionedUsers, setMentionedUsers] = useState(new Map());
    const [isSending, setIsSending] = useState(false);
    const lastClickTimeRef = useRef(0);
    const sentMessagesRef = useRef(new Set());
    // (The specification/folder chat has no DIRECT/CHANNEL concept — always just allMembers.)
    const members = useMemo(() => {
        let memberList = allMembers;
        if (sliceType === 'inbox') {
            if ((conversation === null || conversation === void 0 ? void 0 : conversation.type) === 'DIRECT') {
                return [];
            }
            if ((conversation === null || conversation === void 0 ? void 0 : conversation.type) === 'CHANNEL' && (conversation === null || conversation === void 0 ? void 0 : conversation.members)) {
                memberList = conversation.members.map((member) => ({
                    id: member.user.id,
                    username: member.user.username,
                    email: member.user.email,
                    avatar: member.user.avatar,
                }));
            }
        }
        if (currentUser === null || currentUser === void 0 ? void 0 : currentUser.id) {
            memberList = memberList.filter((member) => member.id !== currentUser.id);
        }
        return memberList;
    }, [sliceType, conversation, allMembers, currentUser]);
    useEffect(() => {
        dispatch(fetchMembers());
    }, [dispatch]);
    const extractMentionsFromText = (text) => {
        const mentionRegex = /@(\w+)/g;
        const mentions = [];
        const matches = Array.from(text.matchAll(mentionRegex));
        matches.forEach((match) => {
            const username = match[1];
            const mentionData = mentionedUsers.get(username);
            if (mentionData) {
                mentions.push(mentionData);
            }
        });
        return mentions;
    };
    const handleAddMessage = (e) => __awaiter(this, void 0, void 0, function* () {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
        if (isAccountReadOnly()) {
            showReadOnlyNotice();
            return;
        }
        const now = Date.now();
        if (now - lastClickTimeRef.current < 500) {
            return;
        }
        lastClickTimeRef.current = now;
        if (form.message !== '' && !isSending) {
            const mentions = extractMentionsFromText(form.message);
            if (form.message.trim() === '') {
                return;
            }
            const messageKey = `${form.message.trim()}_${Date.now()}`;
            if (sentMessagesRef.current.has(messageKey)) {
                return;
            }
            sentMessagesRef.current.add(messageKey);
            if (sentMessagesRef.current.size > 10) {
                const keys = Array.from(sentMessagesRef.current);
                sentMessagesRef.current.clear();
                keys.slice(-5).forEach((key) => sentMessagesRef.current.add(key));
            }
            setIsSending(true);
            // Clear the form immediately to prevent double-sending
            const messageToSend = form.message;
            setForm(Object.assign(Object.assign({}, form), { message: '', image: null }));
            setMentionsState((prev) => (Object.assign(Object.assign({}, prev), { mentions: [] })));
            setMentionedUsers(new Map());
            try {
                yield sendWebSocketMessage({
                    conversationId: conversationId || '',
                    text: messageToSend,
                    image: form.image ? form.image.name : undefined, // Send just the filename for now
                    mentions: mentions.length > 0 ? mentions : undefined,
                });
                // Note: Message will be added to Redux store via WebSocket message processing
                // This ensures consistent message handling for all participants
                if (sliceType === 'inbox' && window) {
                    window.scrollTo({
                        top: document.body.scrollHeight,
                        behavior: 'smooth',
                    });
                }
                onAfterSend === null || onAfterSend === void 0 ? void 0 : onAfterSend();
            }
            catch (error) {
                if (handleReadOnlyError(error)) {
                    setForm((current) => (Object.assign(Object.assign({}, current), { message: messageToSend, image: form.image })));
                    setMentionsState((current) => (Object.assign(Object.assign({}, current), { mentions: mentionsState.mentions })));
                    setMentionedUsers(mentionedUsers);
                    return;
                }
                // Fallback to HTTP if WebSocket fails
                dispatch(addMessage({
                    conversationId: conversationId || '',
                    text: messageToSend,
                    image: form.image,
                    mentions: mentions.length > 0 ? mentions : undefined,
                }))
                    .unwrap()
                    .then(() => {
                    setForm(Object.assign(Object.assign({}, form), { message: '', image: null }));
                    setMentionsState((prev) => (Object.assign(Object.assign({}, prev), { mentions: [] })));
                    setMentionedUsers(new Map());
                    if (sliceType === 'inbox' && window) {
                        window.scrollTo({
                            top: document.body.scrollHeight,
                            behavior: 'smooth',
                        });
                    }
                    onAfterSend === null || onAfterSend === void 0 ? void 0 : onAfterSend();
                })
                    .catch((fallbackError) => {
                    if (!handleReadOnlyError(fallbackError))
                        throw fallbackError;
                    setForm((current) => (Object.assign(Object.assign({}, current), { message: messageToSend, image: form.image })));
                    setMentionsState((current) => (Object.assign(Object.assign({}, current), { mentions: mentionsState.mentions })));
                    setMentionedUsers(mentionedUsers);
                })
                    .finally(() => {
                    setIsSending(false);
                });
            }
            finally {
                setIsSending(false);
            }
        }
    });
    // Picking a file posts it right away, together with whatever text is
    // currently typed — it doesn't wait for the Send button.
    const handleAddImage = (chosenFile) => {
        if (!chosenFile)
            return;
        dispatch(addMessage({
            conversationId: conversationId || '',
            text: form.message,
            image: chosenFile,
        }));
    };
    const handleFormatClick = (marker) => {
        var _a, _b;
        const textarea = inputRef.current;
        if (!textarea)
            return;
        const selectionStart = (_a = textarea.selectionStart) !== null && _a !== void 0 ? _a : form.message.length;
        const selectionEnd = (_b = textarea.selectionEnd) !== null && _b !== void 0 ? _b : form.message.length;
        const result = wrapSelectionWithMarker(form.message, selectionStart, selectionEnd, marker);
        setForm((prev) => (Object.assign(Object.assign({}, prev), { message: result.value })));
        setTimeout(() => {
            if (inputRef.current) {
                inputRef.current.focus();
                inputRef.current.setSelectionRange(result.selectionStart, result.selectionEnd);
            }
        }, 0);
    };
    const handleListClick = (type) => {
        var _a, _b;
        const textarea = inputRef.current;
        if (!textarea)
            return;
        const selectionStart = (_a = textarea.selectionStart) !== null && _a !== void 0 ? _a : form.message.length;
        const selectionEnd = (_b = textarea.selectionEnd) !== null && _b !== void 0 ? _b : form.message.length;
        const result = applyListFormatting(form.message, selectionStart, selectionEnd, type);
        setForm((prev) => (Object.assign(Object.assign({}, prev), { message: result.value })));
        setTimeout(() => {
            if (inputRef.current) {
                inputRef.current.focus();
                inputRef.current.setSelectionRange(result.selectionStart, result.selectionEnd);
            }
        }, 0);
    };
    const handleSelectMention = (user) => {
        var _a;
        const beforeMention = form.message.substring(0, mentionsState.mentionStart);
        const afterMention = form.message.substring(((_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.selectionStart) || form.message.length);
        const mentionText = `@${user.username}`;
        const newMessage = beforeMention + mentionText + afterMention;
        setForm(Object.assign(Object.assign({}, form), { message: newMessage }));
        setMentionsState((prev) => (Object.assign(Object.assign({}, prev), { showDropdown: false })));
        setMentionedUsers((prev) => {
            const updated = new Map(prev);
            updated.set(user.username, { id: user.id, username: user.username });
            return updated;
        });
        setTimeout(() => {
            if (inputRef.current) {
                const newCursorPos = beforeMention.length + mentionText.length;
                inputRef.current.setSelectionRange(newCursorPos, newCursorPos);
                inputRef.current.focus();
            }
        }, 0);
    };
    const handleKeyDown = (e) => {
        if (mentionsState.showDropdown) {
            switch (e.key) {
                case 'ArrowDown':
                    e.preventDefault();
                    setMentionsState((prev) => (Object.assign(Object.assign({}, prev), { selectedIndex: Math.min(prev.selectedIndex + 1, prev.filteredUsers.length - 1) })));
                    break;
                case 'ArrowUp':
                    e.preventDefault();
                    setMentionsState((prev) => (Object.assign(Object.assign({}, prev), { selectedIndex: Math.max(prev.selectedIndex - 1, 0) })));
                    break;
                case 'Enter':
                case ' ':
                    e.preventDefault();
                    if (mentionsState.filteredUsers[mentionsState.selectedIndex]) {
                        handleSelectMention(mentionsState.filteredUsers[mentionsState.selectedIndex]);
                    }
                    break;
                case 'Escape':
                    setMentionsState((prev) => (Object.assign(Object.assign({}, prev), { showDropdown: false })));
                    break;
                default:
                    break;
            }
        }
        else if (e.key === 'Enter' && !e.shiftKey) {
            // Shift+Enter falls through to the textarea's own default behavior — a
            // plain newline, needed to add another line to a list.
            e.preventDefault();
            handleAddMessage();
        }
    };
    const handleInputChange = (e) => {
        const { value, selectionStart } = e.target;
        setForm(Object.assign(Object.assign({}, form), { message: value }));
        const cursorPos = selectionStart || 0;
        const textBeforeCursor = value.substring(0, cursorPos);
        const lastAtIndex = textBeforeCursor.lastIndexOf('@');
        if (lastAtIndex !== -1) {
            const textAfterAt = textBeforeCursor.substring(lastAtIndex + 1);
            if (!/\s/.test(textAfterAt)) {
                const filteredUsers = members
                    .filter((member) => member.username.toLowerCase().includes(textAfterAt.toLowerCase()))
                    .slice(0, 5);
                setMentionsState({
                    showDropdown: filteredUsers.length > 0,
                    filteredUsers,
                    selectedIndex: 0,
                    mentionStart: lastAtIndex,
                    searchText: textAfterAt,
                    mentions: mentionsState.mentions,
                });
            }
            else {
                setMentionsState((prev) => (Object.assign(Object.assign({}, prev), { showDropdown: false })));
            }
        }
        else {
            setMentionsState((prev) => (Object.assign(Object.assign({}, prev), { showDropdown: false })));
        }
    };
    return (React.createElement("div", { className: styles.wrap },
        React.createElement("div", { className: styles.toolbar, onMouseDown: (e) => e.preventDefault() },
            formatButtons.map(({ key, Icon, marker }) => (React.createElement(Button, { key: key, theme: "ghost", color: "secondary", size: "xs", shape: "rounded", leftIcon: React.createElement(Icon, null), onClick: () => handleFormatClick(marker) }))),
            React.createElement("span", { className: styles.divider }),
            listButtons.map(({ key, Icon, type }) => (React.createElement(Button, { key: key, theme: "ghost", color: "secondary", size: "xs", shape: "rounded", leftIcon: React.createElement(Icon, null), onClick: () => handleListClick(type) }))),
            toolbarEnd && React.createElement("div", { className: styles.toolbarEnd }, toolbarEnd)),
        React.createElement("div", { className: classNames(styles.box, { [styles.focused]: isFocused }) },
            React.createElement("textarea", { className: styles.textarea, placeholder: t('MESSAGE_INPUT_PLACEHOLDER'), name: "message", value: form.message, onChange: handleInputChange, onKeyDown: handleKeyDown, onFocus: () => setIsFocused(true), onBlur: () => setIsFocused(false), ref: inputRef }),
            React.createElement("div", { className: styles.actions },
                React.createElement("div", null,
                    React.createElement(FileInput, { allowedExtensions: ['jpg', 'png', 'jpeg'], onSelect: handleAddImage }, (openFileChooser) => (React.createElement(Button, { size: "xs", theme: "ghost", color: "secondary", shape: "rounded", leftIcon: React.createElement(PaperclipSvg, null), onClick: openFileChooser })))),
                React.createElement("div", { className: styles.actionsEnd },
                    React.createElement("p", { className: classNames(styles.shortcutHint, { [styles.visible]: isFocused }) }, t('MESSAGE_INPUT_SHORTCUT_HINT')),
                    React.createElement(Button, { size: "xs", color: "primary", onClick: handleAddMessage, disabled: isSending || form.message.trim() === '', rightIcon: React.createElement(SendSvg, null) }, t('MESSAGE_INPUT_BUTTON')))),
            React.createElement(MentionsDropdown, { users: mentionsState.filteredUsers, selectedIndex: mentionsState.selectedIndex, onSelect: handleSelectMention, visible: mentionsState.showDropdown, inputRef: inputRef }))));
}
export default MessageInput;
